import { useState, type KeyboardEvent } from 'react';
import { Form, Badge, CloseButton } from 'react-bootstrap';

// Pragmatic email check (not RFC-exhaustive): something@something.tld.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isValidEmail(v: string): boolean {
  return EMAIL_RE.test(v.trim());
}

/**
 * Chip-style multi-email input. Type an address and press Enter (or comma) to add it as a
 * removable chip; invalid addresses are rejected with an inline message; duplicates are
 * ignored. Replaces comma-separated free-text so users aren't formatting lists by hand.
 * Pattern ported (shape only) from ignite-frontend's notifications email input.
 */
export function EmailChipsInput({
  value,
  onChange,
  placeholder = 'Enter an email and press Enter',
}: {
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
}) {
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  function add(raw: string) {
    const email = raw.trim();
    if (!email) return;
    if (!isValidEmail(email)) {
      setError(`Invalid email: ${email}`);
      return;
    }
    if (value.includes(email)) {
      setDraft('');
      setError(null);
      return;
    }
    onChange([...value, email]);
    setDraft('');
    setError(null);
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      add(draft);
    } else if (e.key === 'Backspace' && !draft && value.length) {
      // Backspace on an empty field removes the last chip.
      onChange(value.slice(0, -1));
    }
  }

  return (
    <div>
      <Form.Control
        type="text"
        value={draft}
        placeholder={placeholder}
        isInvalid={Boolean(error)}
        onChange={(e) => {
          setDraft(e.target.value);
          if (error) setError(null);
        }}
        onKeyDown={onKeyDown}
        onBlur={() => draft.trim() && add(draft)}
      />
      {error && <Form.Control.Feedback type="invalid" className="d-block">{error}</Form.Control.Feedback>}
      {value.length > 0 && (
        <div className="d-flex flex-wrap gap-2 mt-2">
          {value.map((email) => (
            <Badge
              key={email}
              bg="light"
              text="dark"
              className="d-flex align-items-center gap-1 border"
              style={{ fontWeight: 400 }}
            >
              {email}
              <CloseButton
                aria-label={`Remove ${email}`}
                style={{ fontSize: '0.6rem' }}
                onClick={(e) => { e.preventDefault(); onChange(value.filter((v) => v !== email)); }}
              />
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}
